import { CClient } from '@common/utils'
import Footer from '@components/common/footer'
import NavBar from '@components/common/navbar'
import TabbedSection from '@components/common/tabbed-section'
import GlobalQuizLeaderboard from '@components/quizzes/global-quiz-leaderboard'
import { USER_COUNTRY_FROM_IP_COOKIE } from '@lib/constants/common'
import { COMPETITION_TYPES } from '@lib/constants/competitions'
import { QUIZZES_PAGE_SIZE, QUIZ_LIST_SECTIONS } from '@lib/constants/quizzes'
import { quizzesListSeo } from '@lib/seo'
import { TabType } from '@lib/types/common'
import { CompetititonsListType } from '@lib/types/competitions'
import { StudentProfileSummaryProps } from '@lib/types/gamification'
import { QuizLinkedEntityType, QuizTabType } from '@lib/types/quizzes'
import {
  formatCountryQuery,
  getCountryFromIP,
  getServerSideRedirectResult,
} from '@lib/utils/common'
import { GetServerSideProps } from 'next'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import { parseCookies } from 'nookies'
import useTabs from '@hooks/quizzes/useTabs'
import { getQuizSectionTabs } from '@lib/utils/quizzes'
import { StudentClassLeaderboardResponseType } from '@lib/types/leaderboard'

type Props = {
  tabs: QuizTabType[]
  overallLeaderDetails: StudentProfileSummaryProps
  weeklyLeaderDetails: StudentProfileSummaryProps
}

const QuizzesPage = ({
  tabs,
  overallLeaderDetails,
  weeklyLeaderDetails,
}: Props) => {
  const { activeTab, setActiveTab } = useTabs({
    defaultTab: {
      value: QUIZ_LIST_SECTIONS.LEADERBOARD,
      label: QUIZ_LIST_SECTIONS.LEADERBOARD,
    },
  })
  const router = useRouter()

  return (
    <>
      <NextSeo {...quizzesListSeo} />
      <div className="bg-grey-100">
        <NavBar next={router.asPath} />
        <div className="container px-5 pt-5 pb-10 mx-auto md:px-10">
          <h1 className="pt-8 pb-3 text-2xl leading-tight text-center text-grey lg:text-4xl font-700">
            <span className="underlined-text">Coding quizzes</span> for kids
          </h1>
          <p className="text-center pb-5">
            Great collection of fun and interactive coding quizzes for kids to
            assess and acquire computer science knowledge.
          </p>
          <TabbedSection
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabs={tabs}
            tabFontSize="lg"
          >
            <GlobalQuizLeaderboard
              overallLeaderDetails={overallLeaderDetails}
              weeklyLeaderDetails={weeklyLeaderDetails}
            />
          </TabbedSection>
        </div>
      </div>
      <Footer />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const token = parseCookies(ctx).token
  const countryCode =
    parseCookies(ctx)?.[USER_COUNTRY_FROM_IP_COOKIE] ||
    (await getCountryFromIP(ctx))
  const countryQuery = formatCountryQuery(countryCode)

  // Fetching quizzes to determine which section tabs to show
  const response = await CClient(
    `/api/v1/competitions/?competition_type=${COMPETITION_TYPES.QUIZ_CONTEST}&page_size=${QUIZZES_PAGE_SIZE}${countryQuery}&order_by_quiz_difficulty=Ascending&is_practice=True`,
    {
      headers: {
        Authorization: token ? `Token ${token}` : null,
        'Content-Type': 'application/json',
        cookie: ctx.req.headers.cookie,
      },
    }
  )

  if (!response.ok)
    return getServerSideRedirectResult(ctx, `/${response.status}`)

  const data = await response.json()

  const quizzes: CompetititonsListType<QuizLinkedEntityType> = data.results

  const tabs: TabType[] = getQuizSectionTabs(quizzes)

  const leaderboardResponse = await CClient(
    '/api/v1/quiz-leaderboard/?page_size=1'
  )

  if (!leaderboardResponse.ok)
    return getServerSideRedirectResult(ctx, `/${leaderboardResponse.status}`)

  const leaderboardData: StudentClassLeaderboardResponseType = await leaderboardResponse.json()
  const { overallLeaderDetails, weeklyLeaderDetails } = leaderboardData.results

  return {
    props: {
      tabs,
      overallLeaderDetails,
      weeklyLeaderDetails,
    },
  }
}

export default QuizzesPage
